import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ChevronRight,
  Download,
  FileText,
  GitBranch,
  Scale,
  Sparkles,
} from 'lucide-react';
import './DecisionInterneDetail.css';

/* ────────────────────────── Types ────────────────────────── */

type StatutDecision = 'importe' | 'en-traitement' | 'anonymise' | 'controle' | 'valide';

interface Partie {
  nom: string;
  qualite: string;
  avocat?: string;
}

interface EtapeProcedure {
  date: string;
  acte: string;
}

interface DecisionComplete {
  id: string;
  ref: string;
  numero: string;
  numeroRG: string;
  date: string;
  juridiction: string;
  sigle: string;
  formation: string;
  president: string;
  greffier: string;
  matiere: string;
  typeDecision: string;
  objet: string;
  synthese: string;
  statut: StatutDecision;
  publiee: boolean;
  parties: Partie[];
  procedure: EtapeProcedure[];
  demandes: string[];
  textesCites: string[];
  dispositif: string[];
  motsCles: string[];
  affaire: string;
}

/* ────────────────────────── Labels ────────────────────────── */

const LABELS_STATUT: Record<StatutDecision, string> = {
  importe: 'Importé',
  'en-traitement': 'En traitement',
  anonymise: 'Anonymisé',
  controle: 'Contrôlé',
  valide: 'Validé',
};

/* ────────────────────────── Données mock ────────────────────────── */

const DECISIONS: DecisionComplete[] = [
  {
    id: 'dec-1', ref: 'TCA-2024-1187',
    numero: 'Jugement n° 1187/2024', numeroRG: 'RG n° 2024/1187',
    date: '2024-11-14', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '2e chambre', president: 'Mme N\u2019GUESSAN Aya', greffier: 'M. TRAOR\u00c9 Issa',
    matiere: 'Sûretés et garanties', typeDecision: 'Jugement',
    objet: "Appel en garantie sur caution bancaire solidaire",
    synthese: "Le tribunal retient l'engagement de caution solidaire souscrit par la banque et la condamne au paiement du principal réclamé, avec exécution provisoire partielle.",
    statut: 'controle', publiee: false,
    parties: [
      { nom: 'SOCIÉTÉ IVOMAT SARL', qualite: 'Demanderesse', avocat: 'Me BAMBA Salif' },
      { nom: "BANQUE ATLANTIQUE CÔTE D'IVOIRE", qualite: 'Défenderesse', avocat: 'Cabinet DIALLO & Associés' },
    ],
    procedure: [
      { date: '2024-03-12', acte: "Assignation délivrée par la demanderesse devant le Tribunal de commerce d'Abidjan" },
      { date: '2024-05-20', acte: "Constitution de la défenderesse et dépôt de conclusions en réponse" },
      { date: '2024-09-10', acte: "Audience de plaidoirie — les parties ont développé leurs moyens respectifs" },
    ],
    demandes: [
      "condamner la BANQUE ATLANTIQUE CÔTE D'IVOIRE au paiement de la somme de 51 000 000 FCFA au titre de la caution solidaire",
      "assortir la condamnation de l'exécution provisoire nonobstant toute voie de recours",
      "condamner la défenderesse aux entiers dépens",
    ],
    textesCites: [
      "l'Acte uniforme portant organisation des sûretés (art. 13 et suivants)",
      "le Code de procédure civile, commerciale et administrative (art. 462)",
    ],
    dispositif: [
      "Reçoit la demanderesse en son action",
      "Condamne la BANQUE ATLANTIQUE CÔTE D'IVOIRE à payer à la SOCIÉTÉ IVOMAT SARL la somme de 51 000 000 FCFA",
      "Ordonne l'exécution provisoire à hauteur de 50 % du montant de la condamnation",
      "Condamne la défenderesse aux dépens",
    ],
    motsCles: ['caution bancaire', 'sûreté', 'exécution provisoire', 'recouvrement'],
    affaire: 'AFF-IVOMAT-BATL',
  },
  {
    id: 'dec-2', ref: 'TCA-2025-0921',
    numero: 'Jugement n° 921/2025', numeroRG: 'RG n° 2025/0921',
    date: '2025-07-23', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '1re chambre', president: 'M. KOUAMÉ Koffi', greffier: 'Mme BROU Affoué',
    matiere: 'Droit des sociétés', typeDecision: 'Jugement',
    objet: "Litige relatif à la qualité des parties et immatriculation",
    synthese: "La demanderesse conteste l'immatriculation de la défenderesse au RCCM et sollicite sa radiation pour activité non conforme à l'objet social déclaré.",
    statut: 'anonymise', publiee: false,
    parties: [
      { nom: 'SISSOKO Aïcha Modibo Kane', qualite: 'Demanderesse' },
      { nom: 'COURS SECONDAIRE MARIE HOULDA SARL', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2025-02-05', acte: "Assignation introductive d'instance délivrée par la demanderesse" },
      { date: '2025-04-14', acte: "Audience de mise en état — demande de production de pièces complémentaires" },
      { date: '2025-06-30', acte: "Clôture des débats et mise en délibéré" },
    ],
    demandes: [
      "ordonner la radiation de l'immatriculation de la SARL COURS SECONDAIRE MARIE HOULDA au RCCM",
      "condamner la défenderesse à des dommages-intérêts pour concurrence déloyale",
    ],
    textesCites: [
      "l'Acte uniforme relatif au droit des sociétés commerciales et du groupement d'intérêt économique (art. 27)",
      "l'Acte uniforme relatif au droit commercial général (art. 35 et 44)",
    ],
    dispositif: [
      "Déclare l'action recevable en la forme",
      "Dit que l'immatriculation est conforme aux exigences de l'Acte uniforme",
      "Déboute la demanderesse de l'ensemble de ses demandes",
      "Condamne la demanderesse aux dépens",
    ],
    motsCles: ['RCCM', 'immatriculation', 'objet social', 'radiation'],
    affaire: 'AFF-SISSOKO-CSMH',
  },
  {
    id: 'dec-3', ref: 'TCA-2025-0761',
    numero: 'Jugement n° 761/2025', numeroRG: 'RG n° 2025/0761',
    date: '2025-04-15', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '3e chambre', president: 'M. DIARRA Moussa', greffier: 'Mme KONAN Aya',
    matiere: 'Recouvrement', typeDecision: 'Jugement',
    objet: "Instance en paiement de factures et intérêts de retard",
    synthese: "Le tribunal condamne la partie défenderesse au paiement des factures impayées, majorées des intérêts de droit à compter de la mise en demeure.",
    statut: 'valide', publiee: true,
    parties: [
      { nom: 'SOCIÉTÉ KANZA DISTRIBUTION', qualite: 'Demanderesse', avocat: 'Me OUATTARA' },
      { nom: 'ETS KOUADIO FRÈRES', qualite: 'Défenderesse' },
    ],
    procedure: [
      { date: '2024-11-20', acte: "Mise en demeure adressée au débiteur par lettre recommandée avec accusé de réception" },
      { date: '2025-01-10', acte: "Assignation devant le Tribunal de commerce d'Abidjan" },
      { date: '2025-03-25', acte: "Audience des plaidoiries — conclusions récapitulatives déposées" },
    ],
    demandes: [
      "condamner les ETS KOUADIO FRÈRES au paiement de la somme de 12 500 000 FCFA au titre des factures impayées",
      "assortir la condamnation des intérêts au taux légal à compter de la mise en demeure du 20/11/2024",
      "condamner le défendeur aux dépens",
    ],
    textesCites: [
      "le Code civil (art. 1153 et 1154)",
      "l'Acte uniforme portant sur le droit commercial général (art. 15)",
    ],
    dispositif: [
      "Condamne les ETS KOUADIO FRÈRES au paiement de la somme de 12 500 000 FCFA",
      "Dit que cette somme portera intérêts au taux légal à compter du 20 novembre 2024",
      "Condamne le défendeur aux entiers dépens",
    ],
    motsCles: ['factures impayées', 'intérêts de retard', 'recouvrement', 'mise en demeure'],
    affaire: 'AFF-KANZA-KOUADIO',
  },
  {
    id: 'dec-4', ref: 'TCA-2025-0421',
    numero: 'Ordonnance n° 421/2025', numeroRG: 'RG n° 2025/0421',
    date: '2025-06-04', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: 'Juge des référés', president: 'M. YAO Kouassi', greffier: 'M. TRAORÉ Issa',
    matiere: 'Voies d\u2019exécution', typeDecision: 'Ordonnance',
    objet: "Saisie attribution de créances sur compte bancaire",
    synthese: "Le juge autorise la saisie attribution sur les comptes bancaires du débiteur pour le recouvrement de la créance judiciairement constatée.",
    statut: 'importe', publiee: false,
    parties: [
      { nom: 'SOCIÉTÉ AGRO CI', qualite: 'Demanderesse', avocat: 'Me KONÉ Ibrahim' },
      { nom: "M. KOFFI N\u2019DRI", qualite: 'Défendeur' },
    ],
    procedure: [
      { date: '2025-04-15', acte: "Requête aux fins de saisie attribution déposée par la demanderesse" },
      { date: '2025-05-20', acte: "Audience en chambre du conseil — examen des pièces justificatives" },
    ],
    demandes: [
      "autoriser la saisie attribution des sommes détenues par M. KOFFI N\u2019DRI auprès de ses banques",
      "ordonner la signification de l'ordonnance au tiers saisi et au débiteur",
    ],
    textesCites: [
      "l'Acte uniforme portant organisation des procédures simplifiées de recouvrement et des voies d'exécution (art. 153 à 172)",
    ],
    dispositif: [
      "Autorise la saisie attribution sur les comptes bancaires de M. KOFFI N\u2019DRI",
      "Dit que la signification devra intervenir dans les huit jours de la présente ordonnance",
      "Réserve les dépens",
    ],
    motsCles: ['saisie attribution', 'voies d\u2019exécution', 'comptes bancaires', 'créance'],
    affaire: 'AFF-AGROCI-KOFFI',
  },
  {
    id: 'dec-5', ref: 'TCA-2025-0819',
    numero: 'Jugement n° 819/2025', numeroRG: 'RG n° 2025/0819',
    date: '2025-10-18', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '2e chambre', president: 'Mme N\u2019GUESSAN Aya', greffier: 'M. TRAORÉ Issa',
    matiere: 'Baux commerciaux', typeDecision: 'Jugement',
    objet: "Renouvellement du bail commercial et indemnité d'éviction",
    synthese: "Le refus de renouvellement ouvre droit à une indemnité d'éviction calculée selon la valeur du fonds exploité dans les locaux.",
    statut: 'en-traitement', publiee: false,
    parties: [
      { nom: "ETS N\u2019DA SERVICES", qualite: 'Demandeur' },
      { nom: 'IMMOBILIÈRE LAGUNE', qualite: 'Défenderesse', avocat: 'Cabinet SORO & Partners' },
    ],
    procedure: [
      { date: '2025-05-02', acte: "Congé de bail commercial notifié par le bailleur" },
      { date: '2025-06-15', acte: "Assignation aux fins de renouvellement et indemnité d'éviction" },
      { date: '2025-09-30', acte: "Audience de plaidoirie — production des éléments d'évaluation du fonds" },
    ],
    demandes: [
      "ordonner le renouvellement du bail commercial aux mêmes conditions",
      "à titre subsidiaire, condamner la bailleresse au paiement d'une indemnité d'éviction",
    ],
    textesCites: [
      "l'Acte uniforme relatif au droit commercial général (art. 91 à 127)",
    ],
    dispositif: [
      "Dit que le congé est régulier en la forme",
      "Dit que le refus de renouvellement ouvre droit à indemnité d'éviction",
      "Ordonne une expertise pour évaluer le montant de l'indemnité",
      "Sursoit à statuer sur le surplus",
    ],
    motsCles: ['bail commercial', 'renouvellement', 'indemnité d\u2019éviction', 'expertise'],
    affaire: 'AFF-NDA-LAGUNE',
  },
  {
    id: 'dec-6', ref: 'TCA-2023-3052',
    numero: 'Jugement n° 3052/2023', numeroRG: 'RG n° 2023/3052',
    date: '2023-12-07', juridiction: "Tribunal de commerce d'Abidjan", sigle: 'TCA',
    formation: '1re chambre', president: 'M. KOUAMÉ Koffi', greffier: 'Mme BROU Affoué',
    matiere: 'Bail commercial', typeDecision: 'Jugement',
    objet: "Résiliation de bail pour défaut de paiement des loyers",
    synthese: "Le tribunal prononce la résiliation du bail commercial aux torts du preneur, condamné au paiement des arriérés de loyers et à la libération des lieux.",
    statut: 'valide', publiee: true,
    parties: [
      { nom: 'SCI PLATEAU IMMOBILIER', qualite: 'Demanderesse' },
      { nom: 'RESTAURANT LE JARDIN SARL', qualite: 'Défenderesse', avocat: 'Me TOURÉ Amadou' },
    ],
    procedure: [
      { date: '2023-06-15', acte: "Mise en demeure de payer les loyers échus restée infructueuse" },
      { date: '2023-08-22', acte: "Assignation en résiliation de bail et paiement des arriérés" },
      { date: '2023-11-14', acte: "Audience de plaidoirie — la défenderesse reconnaît partiellement la dette" },
    ],
    demandes: [
      "prononcer la résiliation du bail commercial aux torts du preneur",
      "condamner le preneur au paiement de la somme de 8 400 000 FCFA au titre des arriérés de loyers",
      "ordonner l'expulsion du preneur et la libération des lieux",
    ],
    textesCites: [
      "l'Acte uniforme relatif au droit commercial général (art. 101 et 133)",
      "le Code civil (art. 1741 et 1184)",
    ],
    dispositif: [
      "Prononce la résiliation du bail commercial aux torts du preneur",
      "Condamne le RESTAURANT LE JARDIN SARL au paiement de la somme de 8 400 000 FCFA",
      "Ordonne l'expulsion du preneur dans un délai de deux mois",
      "Condamne la défenderesse aux dépens",
    ],
    motsCles: ['résiliation', 'bail commercial', 'arriérés de loyers', 'expulsion'],
    affaire: 'AFF-PLATEAU-JARDIN',
  },
];

/* ────────────────────────── Helpers ────────────────────────── */

function formatDateFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatDateLongFR(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}

function degreJuridiction(d: DecisionComplete): string {
  const low = d.typeDecision.toLowerCase();
  if (low.includes('arrêt') || low.includes('arret')) return 'Appel';
  return 'Première instance';
}

/* ────────────────────────── Composant principal ────────────────────────── */

const DecisionInterneDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const decision = DECISIONS.find(d => d.id === id);

  if (!decision) {
    return (
      <main className="dd-page">
        <button className="dd-retour" onClick={() => navigate('/espace-tca/decisions-internes')}>
          <ArrowLeft size={16} /> Retour
        </button>
        <div className="dd-introuvable">
          <h1>Décision introuvable</h1>
          <p>Cette référence ne figure pas dans la base du greffe.</p>
        </div>
      </main>
    );
  }

  const d = decision;

  /* Décisions du même dossier (affaire) */
  const decisionsLiees = DECISIONS
    .filter(dec => dec.id !== d.id && dec.affaire === d.affaire)
    .sort((a, b) => b.date.localeCompare(a.date));

  /* Décisions similaires (même matière ou mots-clés communs) */
  const motsClesCourants = new Set(d.motsCles.map(m => m.toLowerCase()));
  const decisionsSimilaires = DECISIONS
    .filter(dec => dec.id !== d.id && dec.affaire !== d.affaire)
    .map(dec => ({
      dec,
      score: (dec.matiere === d.matiere ? 3 : 0) + dec.motsCles.filter(m => motsClesCourants.has(m.toLowerCase())).length,
    }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3)
    .map(({ dec }) => dec);

  return (
    <main className="dd-page">
      {/* Retour */}
      <button className="dd-retour" onClick={() => navigate('/espace-tca/decisions-internes')}>
        <ArrowLeft size={16} /> Retour
      </button>

      {/* En-tête */}
      <section className="dd-header">
        <div className="dd-header-text">
          <h1>{d.juridiction} · {d.numero}</h1>
          <p>{d.numeroRG} · {formatDateFR(d.date)} · {d.matiere} · {degreJuridiction(d)}</p>
        </div>
        <div className="dd-header-actions">
          <button className="dd-btn-secondary"><Download size={15} /> PDF</button>
        </div>
      </section>

      {/* Badges */}
      <div className="dd-badges">
        <span className={`dd-badge-statut dd-statut-${d.statut}`}>{LABELS_STATUT[d.statut]}</span>
        <span className={`dd-badge-pub ${d.publiee ? 'dd-pub-oui' : ''}`}>
          {d.publiee ? 'Publiée' : 'Non publiée'}
        </span>
      </div>

      {/* Contenu 3 colonnes */}
      <div className="dd-content">
        {/* Colonne gauche — Marge */}
        <aside className="dd-marge">
          <div className="dd-marge-inner">
            <p className="dd-marge-label">Marge</p>
            <div className="dd-marge-republique">
              <p className="dd-marge-bold">République de Côte d'Ivoire</p>
              <p>Union — Discipline — Travail</p>
              <p className="dd-marge-bold dd-marge-space">{d.juridiction}</p>
              <p className="dd-marge-bold">{d.formation}</p>
            </div>
            <div className="dd-marge-sep" />
            <p className="dd-marge-numero">{d.numero}</p>
            <p className="dd-marge-rg">{d.numeroRG}</p>
            <p className="dd-marge-objet">{d.objet}</p>
            <div className="dd-marge-sep" />
            <p className="dd-marge-label-small">Décision</p>
            <div className="dd-marge-dispositif">
              {d.dispositif.map((item, i) => <p key={i}>{item}</p>)}
            </div>
            <div className="dd-marge-mention">
              <p className="dd-marge-label-small">Mention</p>
              <p>
                {d.publiee
                  ? 'Décision validée et publiée.'
                  : d.statut === 'valide'
                    ? 'Bon pour publication.'
                    : 'Validation du greffe requise.'}
              </p>
            </div>
          </div>
        </aside>

        {/* Colonne centrale — Texte intégral */}
        <article className="dd-texte">
          <header className="dd-texte-header">
            <Scale size={32} className="dd-texte-icon" />
            <p className="dd-texte-juridiction">{d.juridiction}</p>
            <h2 className="dd-texte-numero">{d.numero}</h2>
            <p className="dd-texte-objet">{d.objet}</p>
            <p className="dd-texte-date">Audience du {formatDateLongFR(d.date)}</p>
          </header>

          <div className="dd-texte-body">
            <p className="dd-texte-center dd-texte-bold">Contradictoire</p>

            <p>
              À l'audience publique de la {d.formation} de la {d.juridiction}, tenue le{' '}
              {formatDateLongFR(d.date)}, sous la présidence de {d.president}, assisté de{' '}
              {d.greffier}, greffier, il a été rendu la décision suivante.
            </p>

            <p className="dd-texte-bold dd-texte-upper">Entre :</p>
            {d.parties.map((partie, index) => (
              <p key={`${partie.nom}-${partie.qualite}`}>
                <strong>{partie.nom}</strong>, {partie.qualite.toLowerCase()}
                {partie.avocat ? `, ayant pour conseil ${partie.avocat}` : ''}
                {index < d.parties.length - 1 ? ' ;' : '.'}
              </p>
            ))}

            <p className="dd-texte-bold dd-texte-upper">Sur la procédure et les prétentions</p>
            {d.procedure.map((etape, i) => (
              <p key={i}>
                Attendu que, le {formatDateLongFR(etape.date)}, {etape.acte.toLowerCase()} ;
              </p>
            ))}
            {d.demandes.map((demande, i) => (
              <p key={i}>
                Attendu qu'il est demandé à la juridiction de faire droit à la prétention suivante : {demande.toLowerCase()} ;
              </p>
            ))}

            <p className="dd-texte-bold dd-texte-upper">Le tribunal</p>
            <p>{d.synthese}</p>
            {d.textesCites.map((texte, i) => (
              <p key={i}>Vu {texte} ;</p>
            ))}
            <p>
              Attendu qu'au regard des pièces produites, des débats et des règles applicables,
              il convient de statuer sur l'ensemble des demandes soumises à la juridiction ;
            </p>

            <p className="dd-texte-center dd-texte-bold dd-texte-par-ces-motifs">Par ces motifs</p>
            <p>
              Statuant publiquement, contradictoirement, en matière {d.matiere.toLowerCase()} et
              conformément à la loi ;
            </p>
            {d.dispositif.map((item, i) => (
              <p key={i}>{item} ;</p>
            ))}
            <p className="dd-texte-prononce">
              Ainsi fait, jugé et prononcé publiquement les jour, mois et an que dessus.
            </p>

            <div className="dd-texte-signatures">
              <p className="dd-texte-bold">Le Président</p>
              <p className="dd-texte-bold">Le Greffier</p>
            </div>
          </div>
        </article>

        {/* Colonne droite — Sidebar */}
        <aside className="dd-sidebar">
          {/* Résumé */}
          <section className="dd-sidebar-card dd-sidebar-resume">
            <h3><FileText size={16} /> Résumé</h3>
            <p>{d.synthese}</p>
          </section>

          {/* Décisions liées */}
          <section className="dd-sidebar-card">
            <h3><GitBranch size={16} /> Décisions liées</h3>
            {decisionsLiees.length > 0 ? (
              <div className="dd-sidebar-list">
                {decisionsLiees.map(dec => (
                  <button
                    key={dec.id}
                    className="dd-sidebar-link"
                    onClick={() => navigate(`/espace-tca/decisions-internes/${dec.id}`)}
                  >
                    <div>
                      <p className="dd-sidebar-link-title">{dec.numero}</p>
                      <p className="dd-sidebar-link-meta">{dec.sigle} · {formatDateFR(dec.date)}</p>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="dd-sidebar-empty">Aucune autre décision dans cette affaire.</p>
            )}
          </section>

          {/* Décisions similaires */}
          <section className="dd-sidebar-card">
            <h3><Sparkles size={16} /> Décisions similaires</h3>
            {decisionsSimilaires.length > 0 ? (
              <div className="dd-sidebar-list">
                {decisionsSimilaires.map(dec => (
                  <button
                    key={dec.id}
                    className="dd-sidebar-link"
                    onClick={() => navigate(`/espace-tca/decisions-internes/${dec.id}`)}
                  >
                    <div>
                      <p className="dd-sidebar-link-title">{dec.numero}</p>
                      <p className="dd-sidebar-link-meta">{dec.matiere} · {dec.objet}</p>
                    </div>
                    <ChevronRight size={16} />
                  </button>
                ))}
              </div>
            ) : (
              <p className="dd-sidebar-empty">Aucune décision similaire identifiée.</p>
            )}
          </section>
        </aside>
      </div>
    </main>
  );
};

export default DecisionInterneDetail;
